/** Общение с сервером. Сессия — в httpOnly-cookie, поэтому токенов тут нет. */

export type Card = {
  path: string
  kind: string
  id: string | null
  title: string
  stage: string | null
  status: string | null
  format: string | null
  rubric: string | null
  direction: string | null
  func: string | null
  method: string | null
  prototype: string | null
  expert: string | null
  tags: string[]
  sources: string[]
  links: string[]
  stageBeforeArchive: string | null
  lastEditor: string | null
  mtimeMs: number
  createdAt: string | null
  updatedAt: string | null
  inferred: string[]
  isNew: boolean
}

export type Schema = {
  stages: Array<{ ключ: string; название: string; решения?: boolean; требует_ссылку?: boolean }>
  statuses: Record<string, string>
  formats: Array<{ ключ: string; название: string; канал: string; текстовый: boolean }>
  rubrics: Array<{ ключ: string; название: string; функция: string }>
  directions: Array<{ ключ: string; название: string; орбита: string }>
  orbits: Record<string, string>
  experts: string[]
}

export type Counters = {
  rubrics: Array<{ key: string; label: string; count: number }>
  directions: Array<{ key: string; label: string; orbit: string; count: number }>
  functions: Array<{ key: string; count: number }>
  total: number
}

export type Board = { cards: Card[]; counters: Counters; lastVisit: number }
export type CardDetails = { card: Card; body: string; mtimeMs: number; children: Card[] }
export type SyncState = { lastPushAt: number | null; pending: number; lastError: string | null }
export type Me = { email: string; lastVisit: number; mailConfigured: boolean }

export class ApiError extends Error {
  constructor(
    readonly status: number,
    message: string,
    readonly currentMtimeMs?: number,
  ) {
    super(message)
  }
}

async function call<T>(path: string, init?: RequestInit): Promise<T> {
  const response = await fetch(path, {
    ...init,
    headers: init?.body ? { 'content-type': 'application/json' } : undefined,
    credentials: 'same-origin',
  })
  const text = await response.text()
  const data: unknown = text ? JSON.parse(text) : null

  if (!response.ok) {
    const payload = data as { error?: string; currentMtimeMs?: number } | null
    throw new ApiError(response.status, payload?.error ?? 'Что-то пошло не так', payload?.currentMtimeMs)
  }
  return data as T
}

function post<T>(path: string, body?: unknown): Promise<T> {
  return call<T>(path, { method: 'POST', body: body === undefined ? undefined : JSON.stringify(body) })
}

/** Фильтры доски. Пустые значения в запрос не попадают. */
export type Filters = {
  stage?: string
  status?: string
  format?: string
  rubric?: string
  direction?: string
  expert?: string
  tag?: string
  query?: string
  onlyNew?: boolean
}

function qs(filters: Filters & Record<string, unknown>): string {
  const params = new URLSearchParams()
  for (const [key, value] of Object.entries(filters)) {
    if (value === undefined || value === '' || value === false) continue
    params.set(key, String(value))
  }
  const s = params.toString()
  return s ? `?${s}` : ''
}

export const api = {
  me: () => call<Me>('/api/auth/me'),
  requestCode: (email: string) => post<{ message: string }>('/api/auth/request-code', { email }),
  verify: (email: string, code: string) => post<{ email: string }>('/api/auth/verify', { email, code }),
  logout: () => post<{ ok: true }>('/api/auth/logout'),

  schema: () => call<Schema>('/api/schema'),
  sync: () => call<SyncState>('/api/sync'),
  retrySync: () => post<SyncState>('/api/sync/retry'),

  board: (filters: Filters) => call<Board>(`/api/board${qs(filters)}`),
  archive: (filters: Filters) => call<Card[]>(`/api/archive${qs(filters)}`),
  queue: () => call<Card[]>('/api/queue'),
  library: (filters: Filters & { limit?: number }) => call<Card[]>(`/api/library${qs(filters)}`),
  markSeen: () => post<{ ok: true }>('/api/seen'),
  reindex: () => post<{ seen: number; cards: number; ms: number }>('/api/reindex'),

  card: (path: string) => call<CardDetails>(`/api/card?path=${encodeURIComponent(path)}`),
  createIncoming: (body: { title: string; text: string; expert?: string }) =>
    post<{ path: string; warning?: string }>('/api/incoming', body),
  createIdea: (body: { title: string; text: string; sourcePath?: string; expert?: string }) =>
    post<{ path: string }>('/api/idea', body),
  expand: (body: { path: string; formats: string[]; rubric: string; direction: string }) =>
    post<{ created: string[] }>('/api/card/expand', body),
  decide: (body: { path: string; action: 'принять' | 'переделать' | 'убрать'; comment?: string; expectedMtimeMs?: number }) =>
    post<{ stage: string | null; status: string }>('/api/card/decide', body),
  advance: (body: { path: string; expectedMtimeMs?: number }) =>
    post<{ stage: string }>('/api/card/advance', body),
  complete: (body: { path: string; links: string[]; expectedMtimeMs?: number }) =>
    post<{ stage: string }>('/api/card/complete', body),
  restore: (path: string) => post<{ stage: string | null }>('/api/card/restore', { path }),
  saveBody: (body: { path: string; body: string; expectedMtimeMs: number }) =>
    post<{ ok: true }>('/api/card/body', body),
  saveLabels: (body: {
    path: string
    labels: { rubric?: string; direction?: string; format?: string; method?: string; prototype?: string; tags?: string[] }
    expectedMtimeMs?: number
  }) => post<{ ok: true }>('/api/card/labels', body),
}
